import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { perusahaanApi } from '../api/perusahaan';
import { appApi } from '../api/app';
import { ApiException } from '../api/client';
import { Alert } from '../components/Alert';
import { Button } from '../components/Button';
import { Card, AccentCard } from '../components/Card';
import { AppLayout } from '../components/Layout';
import type { PerusahaanSummary } from '../types/api';

export function AplikasiBookuPage() {
  const [perusahaanList, setPerusahaanList] = useState<PerusahaanSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    const run = async () => {
      try {
        const list = await perusahaanApi.mine();
        setPerusahaanList(list);
      } catch (err) {
        setError(err instanceof ApiException ? err.message : 'Gagal memuat data perusahaan.');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, []);

  const handleMasukOtomatis = async (idPerusahaan: string) => {
    setActionMessage(null);
    setLoadingId(idPerusahaan);

    // Buka tab kosong DULU di dalam click handler (sync) untuk hindari popup blocker.
    const newTab = window.open('about:blank', '_blank');

    try {
      const result = await appApi.loginOtomatis(idPerusahaan);

      if (newTab && !newTab.closed) {
        newTab.location.href = result.redirectUrl;
        setActionMessage({ kind: 'success', text: 'Aplikasi Booku terbuka di tab baru.' });
      } else {
        // Popup blocker → fallback redirect tab saat ini.
        setActionMessage({ kind: 'success', text: 'Mengarahkan ke aplikasi Booku...' });
        setTimeout(() => {
          window.location.href = result.redirectUrl;
        }, 600);
      }
    } catch (err) {
      if (newTab && !newTab.closed) newTab.close();
      setActionMessage({
        kind: 'error',
        text: err instanceof ApiException ? err.message : 'Gagal masuk ke aplikasi Booku.',
      });
    } finally {
      setLoadingId(null);
    }
  };

  const isAktif = (p: PerusahaanSummary) => p.status === 'Aktif' && !!p.tanggalProvisioning;

  return (
    <AppLayout>
      <AccentCard
        title="Booku — Sistem Akuntansi Terpadu"
        subtitle="Klik 'Masuk' pada perusahaan untuk langsung membuka aplikasi"
      >
        {error && (
          <div className="mb-4">
            <Alert variant="error">{error}</Alert>
          </div>
        )}
        {actionMessage && (
          <div className="mb-4">
            <Alert variant={actionMessage.kind === 'success' ? 'success' : 'error'}>
              {actionMessage.text}
            </Alert>
          </div>
        )}
      </AccentCard>

      <div className="mt-6">
        <Card title="Perusahaan Saya" subtitle={`${perusahaanList.length} perusahaan terdaftar`}>
          {loading ? (
            <div className="text-sm text-gray-500">Memuat daftar perusahaan...</div>
          ) : perusahaanList.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-sm text-gray-600 mb-4">
                Anda belum mendaftarkan perusahaan apa pun.
              </p>
              <Link to="/perusahaan/daftar">
                <Button variant="primary">Daftarkan Perusahaan Pertama</Button>
              </Link>
            </div>
          ) : (
            <>
              <ul className="divide-y divide-gray-100">
                {perusahaanList.map((p) => (
                  <li key={p.idPerusahaan} className="py-3 flex items-center justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-gray-800 truncate">{p.namaPerusahaan}</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        ID: <code className="font-mono">{p.idPerusahaan}</code>
                        {' · '}
                        Didaftarkan {formatDate(p.tanggalDaftar)}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <StatusBadge status={p.status} />
                      <Button
                        variant="primary"
                        size="sm"
                        disabled={!isAktif(p) || loadingId === p.idPerusahaan}
                        onClick={() => handleMasukOtomatis(p.idPerusahaan)}
                      >
                        {loadingId === p.idPerusahaan ? 'Memproses...' : 'Masuk'}
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t border-gray-100">
                <Link to="/perusahaan/daftar">
                  <Button variant="outline">+ Daftarkan Perusahaan Baru</Button>
                </Link>
                <Link
                  to="/aplikasi/login"
                  className="text-xs text-gray-500 hover:text-booku-primary underline"
                >
                  Login dengan password manual (fallback)
                </Link>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Tombol "Masuk" otomatis menggunakan akun klien Anda — tidak perlu password.
                Tombol non-aktif kalau perusahaan belum diaktifkan admin.
              </p>
            </>
          )}
        </Card>
      </div>
    </AppLayout>
  );
}

function StatusBadge({ status }: { status: string }) {
  const color = (() => {
    switch (status) {
      case 'Aktif':
        return 'bg-green-100 text-green-800';
      case 'MenungguApproval':
      case 'MenungguVerifikasi':
        return 'bg-yellow-100 text-yellow-800';
      case 'Ditangguhkan':
        return 'bg-orange-100 text-orange-800';
      case 'Ditolak':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  })();
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${color}`}>
      {status}
    </span>
  );
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return value;
  }
}
